import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * PUANTAJ UÇLARI.
 *
 * Tipler `schema.d.ts`ten TÜRETİLİR; elle yazılmaz.
 *
 * `KotaDurum` 0.25.3'e kadar burada AÇIK birleşimdi (`| (string & {})`) çünkü
 * backend alanı düz `string` döndürüyordu. Artık DB enum'una bağlı ve kapalı:
 * `kotaDurum === 'dolu'` gibi bir yazım hatası derleme anında yakalanır.
 */

export type KotaDurum = Semalar['KotaDurumuDto']['kotaDurum'];

export type DenetimSatiri = Semalar['DenetimSatiriDto'];
export type AylikPuantajGunu = Semalar['AylikPuantajGunuDto'];
export type PuantajKalemi = Semalar['PuantajKalemiDto'];
export type GunOzeti = Semalar['GunOzetiDto'];
export type HareketKaydi = Semalar['HareketKaydiDto'];

export type PuantajDurumu = AylikPuantajGunu['durum'];

/**
 * DÜZELTME GEREKÇELERİ KAPALI LİSTEDİR.
 *
 * Serbest metin olsaydı düzeltme sebepleri sayılamaz ve pilot ölçümü
 * yapılamazdı: "telefon arızası" ile "unuttu" aynı kovaya düşerdi. Kod
 * sunucudaki `DUZELTME_GEREKCELERI` ile birebir aynı olmalı.
 */
export const DUZELTME_GEREKCELERI = [
  { kod: 'unuttu', etiket: 'Basmayı unuttu' },
  { kod: 'telefon_arizasi', etiket: 'Telefon arızası' },
  { kod: 'internet_yok', etiket: 'İnternet yok' },
  { kod: 'vardiya_uzadi', etiket: 'Vardiya uzadı' },
  { kod: 'hatali_basma', etiket: 'Hatalı basma' },
  { kod: 'diger', etiket: 'Diğer' },
] as const;

export type DuzeltmeGerekcesi = (typeof DUZELTME_GEREKCELERI)[number]['kod'];
export type KotaDurumu = Semalar['KotaDurumuDto'];
export type PilotGostergeleri = Semalar['PilotGostergeleriDto'];

/** İstemci tarafı sorgu şekli — hiçbir DTO'nun karşılığı değildir. */
export interface DenetimSorgusu {
  /** Boş = kapsamınızdaki tüm birimler. */
  birimId?: Kimlik;
  baslangic: string;
  bitis: string;
}

/* ------------------------------------------------------------------ */

export function denetimEkrani(sorgu: DenetimSorgusu): Promise<DenetimSatiri[]> {
  return istek<DenetimSatiri[]>('/v1/puantaj/denetim', { sorgu: { ...sorgu } });
}

export function pilotGostergeleri(sorgu: DenetimSorgusu): Promise<PilotGostergeleri> {
  return istek<PilotGostergeleri>('/v1/puantaj/pilot-gostergeleri', { sorgu: { ...sorgu } });
}

export function kotaDurumu(birimId?: Kimlik): Promise<KotaDurumu[]> {
  return istek<KotaDurumu[]>('/v1/puantaj/kota', { sorgu: { birimId } });
}

/* ------------------------------------------------------------------ */
/* Kişi çizelgesi                                                      */
/* ------------------------------------------------------------------ */

/**
 * Bir personelin AYLIK puantajı, gün gün.
 *
 * Uç ay bazlı (`{yil, ay}`) — serbest tarih aralığı değil. Gerekçe: mesai
 * kotası ve dönem kapanışı AY üzerinden işliyor; serbest aralık verilseydi
 * ekranda görünen toplam ile dönem özeti tutmazdı.
 */
export function personelPuantaji(
  atamaId: Kimlik,
  yil: number,
  ay: number,
): Promise<AylikPuantajGunu[]> {
  return istek<AylikPuantajGunu[]>(`/v1/puantaj/personel/${atamaId}`, {
    sorgu: { yil, ay },
  });
}

/** Kendi puantajım — `PUANTAJ_GORUNTULE` yerine `kendi` kapsamı yeterli. */
export function puantajimAylik(yil: number, ay: number): Promise<AylikPuantajGunu[]> {
  return istek<AylikPuantajGunu[]>('/v1/puantaj/benim', { sorgu: { yil, ay } });
}

/**
 * Günün ÖZETİ — kaç basma, hangi işaretler, puantör mü bastı.
 *
 * `hareket/:atamaId/:tarih` ile birlikte kullanılır: özet "ne oldu", hareket
 * listesi "tek tek neler basıldı" sorusunu cevaplıyor. İkisi ayrı uç çünkü
 * özet denetim ekranında tek başına da lazım.
 */
export function gunOzeti(atamaId: Kimlik, tarih: string): Promise<GunOzeti> {
  return istek<GunOzeti>(`/v1/puantaj/ozet/${atamaId}/${tarih}`);
}

export function gunHareketleri(atamaId: Kimlik, tarih: string): Promise<HareketKaydi[]> {
  return istek<HareketKaydi[]>(`/v1/puantaj/hareket/${atamaId}/${tarih}`);
}

/**
 * Şef düzeltmesi.
 *
 * DÜZELTİLEN KAYIT SİLİNMEZ. `duzeltilenId` ve `duzeltilenZaman` zorunlu:
 * eski değer denetim izinde kalır, yeni kayıt onun üzerine EKLENİR. Böylece
 * "şef saati değiştirdi mi" sorusu her zaman cevaplanabilir.
 *
 * `duzeltilenZaman` de gönderilir çünkü `devam_hareketi` partition'lı;
 * yalnız kimlikle satır bulunamaz, partition anahtarı gerekir.
 */
export function puantajDuzelt(girdi: {
  atamaId: Kimlik;
  duzeltilenId: Kimlik;
  duzeltilenZaman: string;
  zaman: string;
  gerekce: DuzeltmeGerekcesi;
  not?: string;
}): Promise<void> {
  return istek<void>('/v1/puantaj/duzelt', { yontem: 'POST', govde: girdi });
}

/** Tek kişinin gününü yeniden hesaplar. Gerekçe ZORUNLU — denetim izine yazılır. */
export function atamaYenidenHesapla(
  atamaId: Kimlik,
  tarih: string,
  gerekce: string,
): Promise<{ toplam: number; hesaplanan: number; atlanan: number }> {
  return istek(`/v1/puantaj/yeniden-hesapla/${atamaId}`, {
    yontem: 'POST',
    govde: { tarih, gerekce },
  });
}
